/**
 * Earnings Calculator Service
 * Handles proper calculation of earnings from hourly rate, annual salary, etc.
 */

import { PayPeriod } from '@/models/payroll.model';

export interface EarningsCalculationInput {
  // Employee type
  isHourly: boolean;
  
  // Hourly employee fields
  hourlyRate?: number;
  regularHours?: number;
  overtimeHours?: number;
  overtimeMultiplier?: number; // Typically 1.5 for time-and-a-half
  
  // Salaried employee fields
  annualSalary?: number;
  payPeriod: PayPeriod;
  
  // Additional earnings
  bonuses?: number;
  commissions?: number;
  otherEarnings?: number;
}

export interface EarningsCalculationResult {
  regularEarnings: number;
  overtimeEarnings: number;
  salaryEarnings: number;
  totalEarnings: number;
  breakdown: {
    regularPay?: number;
    overtimePay?: number;
    salaryPay?: number;
    bonuses: number;
    commissions: number;
    otherEarnings: number;
  };
}

export class EarningsCalculatorService {
  /**
   * Calculate pay periods per year based on pay period type
   */
  static getPayPeriodsPerYear(payPeriod: PayPeriod): number {
    switch (payPeriod) {
      case PayPeriod.weekly:
        return 52;
      case PayPeriod.biWeekly:
        return 26;
      case PayPeriod.semiMonthly:
        return 24;
      case PayPeriod.monthly:
        return 12;
      default:
        return 26; // Default to bi-weekly
    }
  }

  /**
   * Calculate hours per pay period based on pay period type
   * Assumes standard 40-hour work week
   */
  static getStandardHoursPerPeriod(payPeriod: PayPeriod): number {
    switch (payPeriod) {
      case PayPeriod.weekly:
        return 40;
      case PayPeriod.biWeekly:
        return 80;
      case PayPeriod.semiMonthly:
        return 86.67; // 2080 hours / 24 pay periods
      case PayPeriod.monthly:
        return 173.33; // 2080 hours / 12 pay periods
      default:
        return 80; // Default to bi-weekly
    }
  }

  /**
   * Convert annual salary to hourly rate
   * Assumes 2080 work hours per year (40 hours/week * 52 weeks)
   */
  static annualSalaryToHourlyRate(annualSalary: number): number {
    const hoursPerYear = 2080;
    return annualSalary / hoursPerYear;
  }

  /**
   * Convert hourly rate to annual salary
   * Assumes 2080 work hours per year (40 hours/week * 52 weeks)
   */
  static hourlyRateToAnnualSalary(hourlyRate: number): number {
    const hoursPerYear = 2080;
    return hourlyRate * hoursPerYear;
  }

  /**
   * Calculate earnings for a pay period
   */
  static calculateEarnings(input: EarningsCalculationInput): EarningsCalculationResult {
    const {
      isHourly,
      hourlyRate = 0,
      regularHours = 0,
      overtimeHours = 0,
      overtimeMultiplier = 1.5, // Standard time-and-a-half
      annualSalary = 0,
      payPeriod,
      bonuses = 0,
      commissions = 0,
      otherEarnings = 0,
    } = input;

    let regularEarnings = 0;
    let overtimeEarnings = 0;
    let salaryEarnings = 0;

    if (isHourly) {
      // Hourly employee calculation
      if (!hourlyRate || hourlyRate <= 0) {
        throw new Error('Hourly rate is required for hourly employees');
      }

      // Regular pay = regular hours * hourly rate
      regularEarnings = regularHours * hourlyRate;

      // Overtime pay = overtime hours * hourly rate * overtime multiplier
      overtimeEarnings = overtimeHours * hourlyRate * overtimeMultiplier;
    } else {
      // Salaried employee calculation
      if (!annualSalary || annualSalary <= 0) {
        throw new Error('Annual salary is required for salaried employees');
      }

      // Calculate salary per pay period
      const payPeriodsPerYear = this.getPayPeriodsPerYear(payPeriod);
      salaryEarnings = annualSalary / payPeriodsPerYear;

      // For salaried employees, regular hours and overtime are typically not used
      // But if provided, we can calculate additional pay
      if (regularHours > 0 && hourlyRate > 0) {
        // If hourly rate is provided, calculate additional pay
        regularEarnings = regularHours * hourlyRate;
      }
    }

    // Total earnings = base pay + overtime + bonuses + commissions + other earnings
    const totalEarnings = regularEarnings + overtimeEarnings + salaryEarnings + bonuses + commissions + otherEarnings;

    return {
      regularEarnings,
      overtimeEarnings,
      salaryEarnings,
      totalEarnings,
      breakdown: {
        regularPay: isHourly ? regularEarnings : undefined,
        overtimePay: isHourly && overtimeEarnings > 0 ? overtimeEarnings : undefined,
        salaryPay: !isHourly ? salaryEarnings : undefined,
        bonuses,
        commissions,
        otherEarnings,
      },
    };
  }

  /**
   * Calculate gross pay from employee data and pay period
   */
  static calculateGrossPay(
    employeeData: {
      hourlyRate?: number;
      salary?: number;
      isHourly?: boolean;
    },
    payPeriod: PayPeriod,
    hoursWorked?: {
      regular?: number;
      overtime?: number;
    },
    additionalEarnings?: {
      bonuses?: number;
      commissions?: number;
      other?: number;
    }
  ): number {
    const isHourly = employeeData.isHourly ?? (employeeData.hourlyRate != null && employeeData.hourlyRate > 0);
    
    const result = this.calculateEarnings({
      isHourly,
      hourlyRate: employeeData.hourlyRate,
      regularHours: hoursWorked?.regular || 0,
      overtimeHours: hoursWorked?.overtime || 0,
      annualSalary: employeeData.salary,
      payPeriod,
      bonuses: additionalEarnings?.bonuses || 0,
      commissions: additionalEarnings?.commissions || 0,
      otherEarnings: additionalEarnings?.other || 0,
    });

    return result.totalEarnings;
  }

  /**
   * Validate earnings calculation input
   */
  static validateInput(input: EarningsCalculationInput): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (input.isHourly) {
      if (!input.hourlyRate || input.hourlyRate <= 0) {
        errors.push('Hourly rate is required and must be greater than 0');
      }
      if (input.regularHours !== undefined && input.regularHours < 0) {
        errors.push('Regular hours cannot be negative');
      }
      if (input.overtimeHours !== undefined && input.overtimeHours < 0) {
        errors.push('Overtime hours cannot be negative');
      }
    } else {
      if (!input.annualSalary || input.annualSalary <= 0) {
        errors.push('Annual salary is required and must be greater than 0');
      }
    }

    if (input.bonuses !== undefined && input.bonuses < 0) {
      errors.push('Bonuses cannot be negative');
    }
    if (input.commissions !== undefined && input.commissions < 0) {
      errors.push('Commissions cannot be negative');
    }
    if (input.otherEarnings !== undefined && input.otherEarnings < 0) {
      errors.push('Other earnings cannot be negative');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

